import { Store } from '@ngrx/store';
import { OidcSecurityService, StsConfigLoader } from 'angular-auth-oidc-client';
import { State } from 'src/app/store';
import { Permission } from '@bciti/types';
import { ITokenScope } from '../auth.model';
import { SetOrgTagAction } from '../store/auth.actions';
import { extractClaimFromJwt } from '../utils/jwt-claims.util';

const INIT_TIMEOUT_MS = 10_000;

export function oidcAppInitializerFactory(oidcService: OidcSecurityService, configLoader: StsConfigLoader, store: Store<State>): () => Promise<void> {
    return (): Promise<void> => {
        // /callback is handled by CallbackComponent — skip here to avoid double processing
        if (window.location.pathname.startsWith('/callback')) {
            return Promise.resolve();
        }

        // When there is no active clientId, loadConfigs() returns [] so checkAuth() would have
        // no configs and never emit, hanging the APP_INITIALIZER. Call loadConfigs() directly
        // instead — the side effect populates OidcOrgsCacheService for the login dropdown.
        if (!localStorage.getItem('oidc-active-clientId')) {
            return configLoader
                .loadConfigs()
                .toPromise()
                .then(() => undefined);
        }

        // checkAuth() can hang forever when the stored clientId is stale (no matching org
        // in loadConfigs → empty configs → observable never emits). Use a plain setTimeout
        // as a safety net since it bypasses any RxJS scheduler issues.
        return new Promise<void>((resolve) => {
            let resolved = false;
            const safeResolve = (): void => {
                if (!resolved) {
                    resolved = true;
                    resolve();
                }
            };

            // Safety net: force-resolve after timeout so the app always bootstraps
            const timerId = setTimeout(() => safeResolve(), INIT_TIMEOUT_MS);

            oidcService.checkAuth().subscribe({
                next: ({ isAuthenticated, userData, configId, accessToken }) => {
                    clearTimeout(timerId);
                    if (isAuthenticated && configId) {
                        const org = (userData?.org as string) || extractClaimFromJwt(accessToken, 'org') || undefined;
                        const roles: string[] = extractClaimFromJwt(accessToken, 'roles') || [];
                        const scopes: ITokenScope[] = extractClaimFromJwt(accessToken, 'scopes') || [];
                        const permissions: Permission[] = extractClaimFromJwt(accessToken, 'permissions') || [];

                        if (org) {
                            store.dispatch(
                                SetOrgTagAction({
                                    org,
                                    configId,
                                    roles,
                                    scopes,
                                    permissions,
                                }),
                            );
                        }
                    }
                    safeResolve();
                },
                error: () => {
                    clearTimeout(timerId);
                    safeResolve();
                },
            });
        });
    };
}
